import { readShopStorage, writeShopStorage } from '~/utils/shopStorage'

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function readIos(): boolean {
  if (!import.meta.client) return false
  const ua = window.navigator.userAgent
  const iPhone = /iphone|ipod/i.test(ua)
  const iPad = /ipad/i.test(ua) || (ua.includes('Mac') && window.navigator.maxTouchPoints > 1)
  return iPhone || iPad
}

function readIosChrome(): boolean {
  if (!import.meta.client) return false
  return /CriOS/i.test(window.navigator.userAgent)
}

export function usePwa() {
  const shopSlug = useShopSlug()
  const route = useRoute()
  const authStore = useAuthStore()
  const deferredPrompt = useState<BeforeInstallPromptEvent | null>('pwa-deferred-prompt', () => null)
  const isStandalone = useState('pwa-standalone', () => false)
  const isIos = useState('pwa-ios', () => false)
  const isIosChrome = useState('pwa-ios-chrome', () => false)
  const dismissed = useState('pwa-install-dismissed', () => false)

  const canPromptInstall = computed(() => Boolean(deferredPrompt.value))

  const showBanner = computed(() => {
    if (!import.meta.client) return false
    if (!authStore.isAdmin) return false
    if (!/\/admin(\/|$)/.test(route.path)) return false
    if (isStandalone.value || dismissed.value) return false
    return isIos.value || canPromptInstall.value
  })

  function refreshDismissed() {
    dismissed.value = readShopStorage(shopSlug.value, 'pwa_install_dismissed') === '1'
  }

  function detect() {
    if (!import.meta.client) return
    const nav = window.navigator as Navigator & { standalone?: boolean }
    isStandalone.value = Boolean(nav.standalone)
      || window.matchMedia('(display-mode: standalone)').matches
    isIos.value = readIos()
    isIosChrome.value = readIosChrome()
    refreshDismissed()
  }

  function dismiss() {
    writeShopStorage(shopSlug.value, 'pwa_install_dismissed', '1')
    dismissed.value = true
  }

  async function promptInstall(): Promise<boolean> {
    const event = deferredPrompt.value
    if (!event) return false
    await event.prompt()
    const choice = await event.userChoice
    deferredPrompt.value = null
    if (choice.outcome === 'accepted') {
      dismiss()
      return true
    }
    return false
  }

  if (import.meta.client) {
    detect()
    watch(shopSlug, () => refreshDismissed())
  }

  return {
    isStandalone,
    isIos,
    isIosChrome,
    canPromptInstall,
    showBanner,
    dismiss,
    promptInstall,
    detect,
  }
}
